import { useMediaQuery } from '@mui/system';
import React from 'react';
import { Box, Column, Row, Stack } from 'src/components-v2/Layout';
import Icon from 'threads5/Icon';
import styled from 'threads5/themes/styled';
import theme from 'src/components-v2/theme';
import PlanCheckmark, { variant } from './PlanCheckmark';
import { Link } from 'src/components/Inputs';

type PlanValue = boolean | 'contact';

export interface ProductRow {
  productName: string;
  description: string;
  payAsYouGo?: PlanValue;
  growth?: PlanValue;
  custom?: PlanValue;
  url?: string;
}

const getVariantTextColor = (variant: variant) => {
  switch (variant) {
    case 'payg':
      return '#088181';
    case 'growth':
      return '#BB33D7';
    case 'custom':
      return '#0B7BBC';
  }
};

const StyledProductName = styled('div')(({ theme }) => {
  return {
    fontSize: '2.0rem',
    lineHeight: '120%',
    letterSpacing: '-0.3px',
    color: '#003E4A',
    fontWeight: 600,
    marginBottom: '8px',
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.8rem',
      lineHeight: '150%',
      letterSpacing: 'inherit',
    },
  };
});

const StyledDescription = styled('div')(({ theme }) => {
  return {
    fontSize: '1.8rem',
    fontWeight: 400,
    lineHeight: '120%',
    letterSpacing: '-0.27px',
    color: '#003E4A',
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.6rem',
      lineHeight: '150%',
      letterSpacing: 'inherit',
    },
  };
});

const MobileProductRow: React.FC<ProductRow> = ({
  productName,
  description,
  payAsYouGo,
  growth,
  custom,
  url,
}) => {
  return (
    <Column
      xs={24}
      sx={{
        padding: '16px 14px',
        background: '#FFF',
        boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.08)',
        borderRadius: '12px',
      }}
    >
      <StyledProductName
        sx={{
          textDecoration: url && 'underline',
          textDecorationColor: '#003E4A',
          width: 'fit-content',
        }}
      >
        <MaybeLink url={url}>{productName}</MaybeLink>
      </StyledProductName>
      <StyledDescription>{description}</StyledDescription>
      <Stack
        direction='row'
        gap={3}
        sx={{
          borderTop: '1px solid var(--graphic-contrast-low, #EAEFF0)',
          paddingTop: '14px',
          marginTop: '8px',
        }}
      >
        <PayAsYouGoColumn enabled={payAsYouGo} />
        <GrowthColumn enabled={growth} />
        <CustomColumn enabled={custom} />
      </Stack>
    </Column>
  );
};

const ProductColumn = ({
  productName,
  description,
  url,
}: {
  productName: string;
  description: string;
  url: string;
}) => {
  return (
    <Column xs={6}>
      <Stack>
        <StyledProductName
          sx={{
            textDecoration: url && 'underline',
            textDecorationColor: '#003E4A',
          }}
        >
          {productName}
        </StyledProductName>
        <StyledDescription>{description}</StyledDescription>
      </Stack>
    </Column>
  );
};

const EmptyColumn = () => {
  return <Column xs={6} />;
};

const Asterisk = styled('div')(({ theme }) => {
  return {
    fontFamily: theme.typography.plaidSans,
    color: '#747677',
    fontSize: '3rem',
    fontWeight: 600,
    lineHeight: '120%',
    letterSpacing: '-1.5px',

    background: 'linear-gradient(48deg, #D45AED 0%, #F8626E 100%)',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',

    [theme.breakpoints.down('sm')]: {
      fontSize: '1.6rem',
      letterSpacing: '-1px',
      height: '19px',
      lineHeight: 'unset',
    },
  };
});

const StyledMobilePlanText = styled('div')(({ theme }) => {
  return {
    fontSize: '1.2rem',
  };
});

const PlanColumn = ({
  variant,
  enabled,
  text,
}: {
  variant: variant;
  enabled?: PlanValue;
  text: string;
}) => {
  const belowSm = useMediaQuery(theme.breakpoints.down('sm'));

  if (belowSm) {
    let icon = (
      <Icon
        name='Checkmark'
        size='14'
        sx={{
          color: getVariantTextColor(variant),
        }}
      />
    );
    if (enabled === 'contact') {
      icon = <Asterisk>*</Asterisk>;
    } else if (!enabled) {
      icon = (
        <Icon
          name='Close'
          size='14'
          sx={{ color: !enabled ? '#747677' : getVariantTextColor(variant) }}
        />
      );
    }

    return (
      <Stack
        direction='row'
        gap='6px'
        sx={{
          alignItems: 'center',
        }}
      >
        {icon}
        <StyledMobilePlanText
          sx={{
            color: !enabled ? '#747677' : getVariantTextColor(variant),
          }}
        >
          {text}
        </StyledMobilePlanText>
      </Stack>
    );
  }

  if (!enabled) {
    return <EmptyColumn />;
  }
  return (
    <Column
      xs={6}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {enabled === 'contact' ? (
        <Asterisk>*</Asterisk>
      ) : (
        <PlanCheckmark variant={variant} />
      )}
    </Column>
  );
};

const PayAsYouGoColumn = ({ enabled }: { enabled: PlanValue }) => {
  return <PlanColumn variant='payg' enabled={enabled} text='Pay as You Go' />;
};

const GrowthColumn = ({ enabled }: { enabled: PlanValue }) => {
  return <PlanColumn variant='growth' enabled={enabled} text='Growth' />;
};

const CustomColumn = ({ enabled }: { enabled: PlanValue }) => {
  return <PlanColumn variant='custom' enabled={enabled} text='Custom' />;
};

const MaybeLink = ({
  belowSm,
  url,
  children,
}: {
  belowSm?: boolean;
  url?: string;
  children: React.ReactNode;
}) => {
  if (url && !belowSm) {
    return (
      <Link href={url}>
        <a
          style={{
            display: 'contents',
          }}
        >
          {children}
        </a>
      </Link>
    );
  }
  return <>{children}</>;
};

const TableRow: React.FC<ProductRow> = ({
  productName,
  description,
  payAsYouGo,
  growth,
  custom,
  url,
}) => {
  const belowSm = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <MaybeLink url={url} belowSm={belowSm}>
      <Row
        sx={{
          marginBottom: {
            xs: '18px',
            sm: 0,
          },
          position: 'relative',
          '&:hover': {
            '.table-row-background': {
              opacity: 1,
            },
          },
        }}
        lgOffset={2}
        lg={20}
      >
        <Box
          className='table-row-background'
          sx={{
            transition: 'opacity 0.3s ease',
            opacity: 0,
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: {
              sm: 'var(--gradient-background-stark-elizabeth, linear-gradient(48deg, #F3EBFE 0%, #E5EFFE 50%, #CDF5FD 100%))',
            },
          }}
        />
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            padding: { sm: '20px 25px' },
            justifyContent: 'space-between',
            minHeight: '104px',
            zIndex: 1,
          }}
        >
          {belowSm ? (
            <MobileProductRow
              productName={productName}
              description={description}
              payAsYouGo={payAsYouGo}
              growth={growth}
              custom={custom}
              url={url}
            />
          ) : (
            <>
              <ProductColumn
                productName={productName}
                description={description}
                url={url}
              />
              <PayAsYouGoColumn enabled={payAsYouGo} />
              <GrowthColumn enabled={growth} />
              <CustomColumn enabled={custom} />
            </>
          )}
        </Box>
      </Row>
    </MaybeLink>
  );
};

export default TableRow;
